import type { TaskAssigneesEditableTask } from "@components/modules/resource-planning/tasks/components/task-assignees/variants/editable/types";
import { TasksListingStore } from "@components/modules/resource-planning/tasks/listing/store";
import type { TasksBulkActionsStore } from "@components/modules/resource-planning/tasks/listing/store/bulk-actions";
import type { TasksFiltersStore } from "@components/modules/resource-planning/tasks/store/filters";
import type {
  TasksViewModeStore,
  TTasksViewMode,
} from "@components/modules/resource-planning/tasks/store/view-mode";
import type {
  TaskBaseFragment,
  TasksFilterOptionsQuery,
  TaskUserFragment,
} from "@src/__generated__/graphql";
import type { AppStore } from "@src/stores/AppStore";
import { action, computed, makeObservable } from "mobx";

export class TasksStore {
  constructor(private appStore: AppStore) {
    makeObservable(this);
  }

  @computed
  get viewStore() {
    return this.appStore.tasksViewModeStore.isBoard
      ? this.appStore.tasksKanbanStore
      : this.appStore.tasksListingStore;
  }

  @computed
  get viewModeStore(): TasksViewModeStore {
    return this.appStore.tasksViewModeStore;
  }

  @computed
  get filtersStore(): TasksFiltersStore {
    return this.viewStore.filtersStore;
  }

  /** Switches the view and carries the applied filters over to it. */
  @action.bound
  setViewMode(viewMode: TTasksViewMode): void {
    const previousFiltersStore = this.filtersStore;
    this.viewModeStore.setViewMode(viewMode);
    if (this.filtersStore === previousFiltersStore) return;

    this.filtersStore.syncFrom(previousFiltersStore);
  }

  @computed
  get bulkActionsStore(): TasksBulkActionsStore {
    return this.viewStore.bulkActionsStore;
  }

  /**
   * Each view keeps its own filters, but the options are loaded once — both
   * get them, or switching the view leaves the other without statuses.
   */
  @computed
  get allFiltersStores(): TasksFiltersStore[] {
    return [
      this.appStore.tasksListingStore.filtersStore,
      this.appStore.tasksKanbanStore.filtersStore,
    ];
  }

  @action.bound
  setFilterOptions(data: TasksFilterOptionsQuery): void {
    for (const filtersStore of this.allFiltersStores) {
      filtersStore.setFilterOptions(data);
    }
  }

  @computed
  get assignees() {
    return (task: TaskBaseFragment) => {
      const assignees = new Map<string, TaskUserFragment>();

      for (const position of task.positions) {
        const user = position.user;
        if (!user || assignees.has(user.id)) continue;
        assignees.set(user.id, user);
      }

      return Array.from(assignees.values());
    };
  }

  @computed
  get currentAssignee() {
    return (task: TaskBaseFragment): TaskUserFragment | null => {
      if (!this.appStore.workspaceStore.settings?.use_current_assignee) {
        return null;
      }

      return task.currentAssignee ?? null;
    };
  }

  /** Board and listing tasks in the shape `TaskAssigneesEditable` reads. */
  assigneesTask(task: TaskBaseFragment): TaskAssigneesEditableTask {
    return {
      id: task.id,
      projectId: task.ourWorkBudgetItem?.project.id,
      assignees: this.assignees(task),
      currentAssignee: this.currentAssignee(task),
    };
  }

  @computed
  get showSubtasksToggle() {
    if (!(this.viewStore instanceof TasksListingStore)) {
      return false;
    }

    return (
      !this.filtersStore.showSubtasksAsTasks &&
      this.appStore.authStore.isInternalUser
    );
  }

  @computed
  get subtasksOpen() {
    if (!(this.viewStore instanceof TasksListingStore)) {
      return false;
    }

    return this.viewStore.subtasksOpen;
  }

  @action.bound
  toggleSubtasksOpen() {
    if (!(this.viewStore instanceof TasksListingStore)) {
      return;
    }

    return this.viewStore.toggleSubtasksOpen();
  }
}
